import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Bell, BellRing, BellOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

export function BellButton({ creatorUserId, isFollowing, size = "icon" }) {
  const { isAuthenticated, getAuthHeaders, user } = useAuth();
  const [bellEnabled, setBellEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated || !creatorUserId || creatorUserId === user?.user_id) return;
    try {
      const res = await fetch(`${API}/bell/status/${creatorUserId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBellEnabled(data.bell_enabled);
      }
    } catch { /* silent */ }
  }, [isAuthenticated, creatorUserId, getAuthHeaders, user?.user_id]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (!isAuthenticated || !isFollowing || creatorUserId === user?.user_id) return null;

  const toggle = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/bell/toggle/${creatorUserId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setBellEnabled(data.bell_enabled);
        toast.success(data.message);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={bellEnabled ? "default" : "outline"}
      size={size}
      onClick={toggle}
      disabled={loading}
      className={bellEnabled
        ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30"
        : "text-muted-foreground hover:text-foreground"
      }
      title={bellEnabled ? "Notifications on — click to turn off" : "Get notified when this creator goes live or posts"}
      data-testid="bell-toggle-btn"
    >
      {bellEnabled ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
    </Button>
  );
}
